import {
  Card,
  Row,
  Col,
  Typography,
  Button,
  Form,
  DatePicker,
  Input,
  Pagination,
} from "antd";
import { DownloadOutlined } from "@ant-design/icons";
import { useEffect, useRef, useState } from "react";
import { useDispatch } from "react-redux";
import { useSearchParams } from "react-router-dom";
import debounce from "lodash/debounce";
import dayjs from "dayjs";
import toast from "react-hot-toast";

import * as API from "../../../../utils/helper/enum";
import globalRequest from "../../../../utils/global/globalRequest";
import * as Validation from "../../../../utils/helper/validation";
import {
  formatDateFunction,
  exportCSV,
  exportPDf,
  roundToDecimal,
} from "../../../../utils/helper/utils";
import { changeLoader } from "../../../../redux/reducers/loader";
import Wallet from "../Wallet";

const { Text } = Typography;
const { RangePicker } = DatePicker;
const { Search } = Input;

const WalletHistory = ({ userId }) => {
  const dispatch = useDispatch();
  const [searchParams, setSearchParams] = useSearchParams();

  const [walletBalance, setWalletBalance] = useState(0);
  const [walletHistory, setWalletHistory] = useState([]);
  const [totalItems, setTotalItems] = useState(0);
  const [showWalletPopup, setShowWalletPopup] = useState(false);

  const [filters, setFilters] = useState({
    page: Number(searchParams.get("page")) || 1,
    limit: 10,
    search: searchParams.get("search") || "",
    start_date: searchParams.get("start_date") || null,
    end_date: searchParams.get("end_date") || null,
  });

  useEffect(() => {
    const params = {};
    Object.entries(filters).forEach(([key, value]) => {
      if (value) params[key] = value;
    });
    setSearchParams(params);
  }, [filters, setSearchParams]);


  const getWalletBalance = async () => {
    dispatch(changeLoader(true));
    try {
      const res = await globalRequest(
        `${API.MANAGE_VENDOR.GET_WALLET_BALANCE}/${userId}`,
        "GET",
        {},
        {},
        true
      );
      if (res?.data?.status === "SUCCESS") {
        setWalletBalance(res.data.data || 0);
      } else toast.error(res?.data?.message);
    } catch (e) {
      toast.error(e.message);
    }
    dispatch(changeLoader(false));
  };

  const getWalletBalanceHistory = async () => {
    dispatch(changeLoader(true));
    try {
      const params = {
        page: filters.page,
        limit: filters.limit,
      };

      if (filters.search) params.search = filters.search;
      if (filters.start_date) params.start_date = filters.start_date;
      if (filters.end_date) params.end_date = filters.end_date;

      const res = await globalRequest(
        `${API.MANAGE_VENDOR.GET_WALLET_BALANCE_HISTORY}/${userId}`,
        "GET",
        {},
        { params },
        true
      );

      if (res?.data?.status === "SUCCESS") {
        setWalletHistory(res.data.data.data || []);
        setTotalItems(res.data.data.paginator.itemCount || 0);
      } else toast.error(res?.data?.message);
    } catch (e) {
      toast.error(e.message);
    }
    dispatch(changeLoader(false));
  };

  useEffect(() => {
    if (userId) {
      getWalletBalance();
      getWalletBalanceHistory();
    }
  }, [userId, filters]);


  const searchDebounce = useRef(
    debounce((value) => {
      setFilters((prev) => ({
        ...prev,
        page: 1,
        search: value,
      }));
    }, 500)
  ).current;

  useEffect(() => {
    return () => searchDebounce.cancel();
  }, []);


  const exportData = async (type) => {
    dispatch(changeLoader(true));
    try {
      const params = { ...filters, type: "export" };

      const res = await globalRequest(
        `${API.MANAGE_VENDOR.GET_WALLET_BALANCE_HISTORY}/${userId}`,
        "GET",
        {},
        { params },
        true
      );

      if (res?.data?.status === "SUCCESS") {
        const rows = [...res.data.data.data];

        const formatted = rows.map((item) => ({
          Date: formatDateFunction(item.createdAt),
          Amount: roundToDecimal(item.amount, 2),
          Type: item.transactionDetails?.type,
          Balance: roundToDecimal(item.walletClosingBalance, 2),
          Note: item.additionalNote || "-",
        }));

        if (type === "csv") {
          exportCSV(formatted, "wallet-history.csv");
        } else {
          exportPDf(
            Object.keys(formatted[0]),
            formatted.map((i) => Object.values(i)),
            "wallet-history.pdf"
          );
        }
      }
    } catch (e) {
      toast.error(e.message);
    }
    dispatch(changeLoader(false));
  };

  const renderTransactionDetails = (item) => {
    if (item?.additionalNote) return item.additionalNote;

    const addedBy =
      userId === item?.addedBy
        ? item?.transactionDetails?.card_number
        : "Admin";

    return item?.transactionDetails?.type === "credit"
      ? `Money added to wallet from ${addedBy}`
      : `Money deducted from wallet by ${addedBy}`;
  };

  return (
    <>
      <Row gutter={[24, 24]}>
        <Col span={24}>
          <Card bordered={false}>
            {/* Wallet Header */}
            <div className="flex justify-between items-center mb-6 bg-gray-200 p-5">
              <div>
                <Text strong>SAR {walletBalance}</Text>
                <br />
                <Text>Wallet Balance</Text>
              </div>

              <Button type="link" onClick={() => setShowWalletPopup(true)}>
                <Text underline strong>
                  Top up Wallet
                </Text>
              </Button>
            </div>

            {/* Filters */}
            <Form layout="vertical">
              <div className="flex flex-wrap gap-4 mb-6">
                <RangePicker
                  format="YYYY-MM-DD"
                  value={
                    filters.start_date && filters.end_date
                      ? [
                          dayjs(filters.start_date),
                          dayjs(filters.end_date),
                        ]
                      : null
                  }
                  onChange={(dates) =>
                    setFilters((prev) => ({
                      ...prev,
                      page: 1,
                      start_date: dates
                        ? dayjs(dates[0]).format("YYYY-MM-DD")
                        : null,
                      end_date: dates
                        ? dayjs(dates[1]).format("YYYY-MM-DD")
                        : null,
                    }))
                  }
                />

                <Search
                  placeholder="Search"
                  defaultValue={filters.search}
                  style={{
                      width: window.innerWidth < 768 ? "100%" : 250
                    }}
                  className=" cursor-pointer"
                  onChange={(e) =>
                    searchDebounce(
                      Validation.strFormat(e.target.value, "trim")
                    )
                  }
                />

                <Button
                  icon={<DownloadOutlined />}
                  onClick={() => exportData("csv")}
                  type="primary"
                   size={14}
                >
                  CSV
                </Button>

                <Button
                  icon={<DownloadOutlined />}
                  onClick={() => exportData("pdf")}
                  type="primary"
                   size={14}

                >
                  PDF
                </Button>
              </div>
            </Form>

            {/* Table */}
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="p-3 text-left">Transaction Date</th>
                    <th className="p-3 text-left">Delivery Date</th>
                    <th className="p-3 text-left">Order Number</th>
                    <th className="p-3 text-left">Type</th>
                    <th className="p-3 text-left">Amount</th>
                    <th className="p-3 text-left">Closing Balance</th>
                    <th className="p-3 text-left">Description</th>
                  </tr>
                </thead>
                <tbody>
                  {walletHistory.map((item) => (
                    <tr key={item.id} className="border-b">
                     
                      <td className="p-3">
                        {formatDateFunction(item.createdAt)}
                      </td>
                      <td className="p-3">
                        {formatDateFunction(item.deliveryDate)}
                      </td>
                      <td className="p-3">
                        {item.orderId || "-"}
                      </td>
                       <td className="p-3">
                        {item.transactionDetails?.type}
                      </td>
                      <td
                        className={`p-3 font-semibold ${
                          item.transactionDetails?.type === "credit"
                            ? "text-green-600"
                            : "text-red-500"
                        }`}
                      >
                        {item.transactionDetails?.type !== "credit" && "-"}
                        SAR {item.amount}
                      </td>
                      <td className="p-3">
                        SAR {item.walletClosingBalance}
                      </td>
                       <td className="p-3">
                        {renderTransactionDetails(item)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            {totalItems > 0 && (
              <div className="flex justify-end mt-4">
                <Pagination
                  current={filters.page}
                  pageSize={filters.limit}
                  total={totalItems}
                  onChange={(page) =>
                    setFilters((prev) => ({ ...prev, page }))
                  }
                />
              </div>
            )}
          </Card>
        </Col>
      </Row>

      {showWalletPopup && (
        <Wallet
          userId={userId}
          showWalletPopup={showWalletPopup}
          setShowWalletPopup={setShowWalletPopup}
          getWalletBalance={getWalletBalance}
          getWalletBalanceHistory={getWalletBalanceHistory}
        />
      )}
    </>
  );
};

export default WalletHistory;
